import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useShoppingList, type ShoppingItem } from '../hooks/useShoppingList';

type Category = 'all' | 'fertilizers' | 'soils' | 'amendments';

const CATEGORY_CONFIG: Record<Category, { label: string; icon: string; color: string }> = {
  all: { label: 'All', icon: '📋', color: 'text-editorial-black' },
  fertilizers: { label: 'Fertilizers', icon: '🍼', color: 'text-amber-400' },
  soils: { label: 'Soils', icon: '🪴', color: 'text-emerald-400' },
  amendments: { label: 'Amendments', icon: '🧪', color: 'text-blue-400' },
};

function CategoryBadge({ category }: { category: ShoppingItem['category'] }) {
  const config = CATEGORY_CONFIG[category];
  return (
    <span className={`text-xs font-sans font-medium ${config.color}`}>
      {config.icon} {config.label}
    </span>
  );
}

function ShoppingItemCard({ item, onPlantClick }: { item: ShoppingItem; onPlantClick: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-editorial-black truncate">{item.name}</h3>
          <div className="mt-1 flex items-center gap-2">
            <CategoryBadge category={item.category} />
            <span className="text-xs font-sans text-gray-400">
              {item.plants.length} {item.plants.length === 1 ? 'plant' : 'plants'}
            </span>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-editorial-black hover:border-gray-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-expanded={expanded}
          aria-label={expanded ? 'Hide plants' : 'Show plants'}
        >
          <motion.span
            className="material-symbols-outlined"
            style={{ fontSize: 16 }}
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            aria-hidden="true"
          >
            expand_more
          </motion.span>
        </button>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-sans text-gray-400 uppercase tracking-wider mb-2">Used by</p>
              <div className="flex flex-wrap gap-2">
                {item.plants.map((plant) => (
                  <button
                    key={plant.id}
                    onClick={() => onPlantClick(plant.id)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-serif text-editorial-black hover:border-gray-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span className="material-symbols-outlined text-status-green" style={{ fontSize: 14 }} aria-hidden="true">
                      eco
                    </span>
                    {plant.name}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ShoppingList() {
  const navigate = useNavigate();
  const { items, loading, error, totalProducts, plantsWithCareInfo } = useShoppingList();
  const [selectedCategory, setSelectedCategory] = useState<Category>('all');

  const filteredItems = useMemo(() => {
    if (selectedCategory === 'all') return items;
    return items.filter((item) => item.category === selectedCategory);
  }, [items, selectedCategory]);

  const categoryCounts = useMemo(() => {
    const counts: Record<Category, number> = { all: items.length, fertilizers: 0, soils: 0, amendments: 0 };
    items.forEach((item) => {
      counts[item.category]++;
    });
    return counts;
  }, [items]);

  const handlePlantClick = (plantId: string) => {
    navigate(`/plant/${plantId}`);
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-editorial-black hover:border-gray-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Go back"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
              arrow_back
            </span>
          </button>
          <div>
            <h1 className="font-display text-2xl font-bold text-editorial-black">Shopping List</h1>
            <p className="mt-0.5 text-sm font-serif text-gray-500">
              {totalProducts} {totalProducts === 1 ? 'product' : 'products'} from {plantsWithCareInfo}{' '}
              {plantsWithCareInfo === 1 ? 'plant' : 'plants'}
            </p>
          </div>
        </div>
      </div>

      {/* Category filter tabs */}
      <div className="px-6 pb-4">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {(Object.keys(CATEGORY_CONFIG) as Category[]).map((category) => {
            const config = CATEGORY_CONFIG[category];
            const count = categoryCounts[category];
            const isSelected = selectedCategory === category;

            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-sans font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  isSelected
                    ? 'bg-editorial-black text-white'
                    : 'border border-gray-200 bg-white text-gray-500 hover:text-editorial-black hover:border-gray-400'
                }`}
                aria-pressed={isSelected}
              >
                <span>{config.icon}</span>
                <span>{config.label}</span>
                <span
                  className={`ml-1 rounded-full px-1.5 py-0.5 text-xs ${
                    isSelected ? 'bg-white/20' : 'bg-gray-100'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12" role="status" aria-live="polite">
          <span className="material-symbols-outlined animate-spin text-primary" style={{ fontSize: 32 }} aria-hidden="true">
            progress_activity
          </span>
          <p className="mt-4 text-sm font-serif text-gray-500">Loading shopping list...</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="mx-6 rounded-lg bg-danger/5 border border-danger/20 p-4 text-center" role="alert">
          <p className="font-serif text-danger">Failed to load shopping list. Please try again.</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <span className="material-symbols-outlined text-gray-300" style={{ fontSize: 48 }} aria-hidden="true">
            shopping_cart
          </span>
          <h2 className="mt-4 font-display text-lg font-bold text-editorial-black">No products yet</h2>
          <p className="mt-2 text-center text-sm font-serif text-gray-500 max-w-xs">
            Add plants with enhanced care guides to see recommended products here.
          </p>
          <button
            onClick={() => navigate('/add')}
            className="btn-editorial mt-6 max-w-xs"
          >
            <span>Add a plant</span>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">
              arrow_forward
            </span>
          </button>
        </div>
      )}

      {/* Filtered empty state */}
      {!loading && !error && items.length > 0 && filteredItems.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <p className="text-sm font-serif text-gray-500">No {CATEGORY_CONFIG[selectedCategory].label.toLowerCase()} found.</p>
          <button
            onClick={() => setSelectedCategory('all')}
            className="mt-3 text-sm font-sans font-medium text-editorial-black underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            Show all products
          </button>
        </div>
      )}

      {/* Product list */}
      {!loading && !error && filteredItems.length > 0 && (
        <div className="px-6 pb-8">
          <div className="flex flex-col gap-3">
            {filteredItems.map((item, index) => (
              <motion.div
                key={`${item.category}-${item.name}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.2 }}
              >
                <ShoppingItemCard item={item} onPlantClick={handlePlantClick} />
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
